import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axiosInstance from '../../api/Axios';
import UserPermissionsDebug from '../../components/UserPermissionsDebug';
import BMOUserPermissionAssignment from '../../components/BMOUserPermissionAssignment';

const BMOUserAdminDashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState({
    totalStudents: 0,
    activeBursaries: 0,
    pendingApplications: 0,
    totalDisbursements: 0,
    bmoInfo: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // The correct endpoint for BMO User Admin dashboard
      const response = await axiosInstance.get('/api/accounts/bmo-user-admin/dashboard/');
      setDashboardData(response.data);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <h4 className="alert-heading">Error</h4>
        <p>{error}</p>
        <button className="btn btn-outline-danger" onClick={fetchDashboardData}>
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Debug Permissions */}
      <UserPermissionsDebug />
      {/* Debug Permissions Section */}
      <UserPermissionsDebug />

      {/* Welcome Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card bg-primary text-white">
            <div className="card-body">
              <h1 className="card-title mb-2">
                <i className="bi bi-person-badge me-2"></i>
                BMO User Admin Dashboard
              </h1>
              <p className="card-text mb-0">
                Welcome back, {user?.email}
              </p>
              {dashboardData.bmoInfo && (
                <p className="card-text">
                  <small>Managing: {dashboardData.bmoInfo.name}</small>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="row mb-4">
        <div className="col-md-3 mb-3">
          <div className="card bg-info text-white h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="card-title">Total Students</h5>
                  <h2 className="card-text">{dashboardData.totalStudents}</h2>
                </div>
                <div>
                  <i className="bi bi-people fs-1"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3 mb-3">
          <div className="card bg-success text-white h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="card-title">Active Bursaries</h5>
                  <h2 className="card-text">{dashboardData.activeBursaries}</h2>
                </div>
                <div>
                  <i className="bi bi-award fs-1"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3 mb-3">
          <div className="card bg-warning text-white h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="card-title">Pending Applications</h5>
                  <h2 className="card-text">{dashboardData.pendingApplications}</h2>
                </div>
                <div>
                  <i className="bi bi-clock-history fs-1"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3 mb-3">
          <div className="card bg-danger text-white h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="card-title">Total Disbursements</h5>
                  <h2 className="card-text">R {dashboardData.totalDisbursements?.toLocaleString() || 0}</h2>
                </div>
                <div>
                  <i className="bi bi-currency-dollar fs-1"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="bi bi-lightning me-2"></i>
                Quick Actions
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4 mb-3">
                  <button className="btn btn-outline-primary w-100 p-3">
                    <i className="bi bi-person-plus d-block fs-2 mb-2"></i>
                    Manage Students
                  </button>
                </div>
                <div className="col-md-4 mb-3">
                  <button className="btn btn-outline-success w-100 p-3">
                    <i className="bi bi-award d-block fs-2 mb-2"></i>
                    Manage Bursaries
                  </button>
                </div>
                <div className="col-md-4 mb-3">
                  <button className="btn btn-outline-info w-100 p-3">
                    <i className="bi bi-file-earmark-text d-block fs-2 mb-2"></i>
                    Generate Reports
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="row">
        <div className="col-md-6 mb-4">
          <div className="card h-100">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="bi bi-clock-history me-2"></i>
                Recent Applications
              </h5>
            </div>
            <div className="card-body">
              <div className="text-muted text-center py-4">
                <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                <p>No recent applications to display</p>
                <small>Applications will appear here when submitted</small>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-6 mb-4">
          <div className="card h-100">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Pending Actions
              </h5>
            </div>
            <div className="card-body">
              <div className="text-muted text-center py-4">
                <i className="bi bi-check-circle fs-1 d-block mb-2"></i>
                <p>All caught up!</p>
                <small>No pending actions at this time</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* System Information */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="bi bi-info-circle me-2"></i>
                BMO Information
              </h5>
            </div>
            <div className="card-body">
              {dashboardData.bmoInfo ? (
                <div className="row">
                  <div className="col-md-6">
                    <p><strong>BMO Name:</strong> {dashboardData.bmoInfo.name}</p>
                    <p><strong>BMO Code:</strong> {dashboardData.bmoInfo.code}</p>
                    <p><strong>Domain:</strong> {dashboardData.bmoInfo.domain}</p>
                  </div>
                  <div className="col-md-6">
                    <p><strong>Status:</strong> 
                      <span className={`badge ms-2 ${dashboardData.bmoInfo.active ? 'bg-success' : 'bg-warning'}`}>
                        {dashboardData.bmoInfo.active ? 'Active' : 'Inactive'}
                      </span>
                    </p>
                    <p><strong>Self Administered:</strong> 
                      <span className={`badge ms-2 ${dashboardData.bmoInfo.selfadminister ? 'bg-info' : 'bg-secondary'}`}>
                        {dashboardData.bmoInfo.selfadminister ? 'Yes' : 'No'}
                      </span>
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-muted">BMO information not available</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BMOUserAdminDashboard;
